import styled, { css } from 'styled-components';
import { ErrorMessage } from 'formik';
import { snakeCase } from 'lodash';
import { useState } from 'react';

import { Checkmark, Caret } from 'shared/icons';
import { IMenuItem } from 'shared/types/MenuItem';
import { Loader } from 'shared/components';
import { variableFontRegular } from 'styles/theme';
import { useKeyDown, useOutsideClick } from 'shared/hooks';
import { UnorderedList } from '../UnorderedList';

interface ICustomDropdownMenuSelector {
  'data-qa': string;
  disabled?: boolean;
  displayValue?: string;
  hasFormikError?: boolean;
  hasSearch?: boolean;
  isLoading?: boolean;
  label: string;
  menuItems: Array<IMenuItem>;
  name?: string;
  notFoundMessage?: string;
  onBlur?: () => void;
  onMenuItemClick: (menuItem: IMenuItem) => void;
  selection: IMenuItem;
  darkTheme?: boolean;
}

export const CustomDropdownMenuSelector = ({
  'data-qa': dataQa,
  disabled = false,
  displayValue,
  hasFormikError = false,
  hasSearch = false,
  isLoading = false,
  label,
  menuItems,
  name,
  notFoundMessage = 'Not found',
  onBlur,
  onMenuItemClick,
  selection,
  darkTheme,
}: ICustomDropdownMenuSelector) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeMenuItemIndex, setActiveMenuItemIndex] = useState<number | null>(null);
  const regExp = new RegExp(searchTerm, 'i');
  const dropdownMenuRef = useOutsideClick(() => {
    setIsMenuOpen(false);
  });

  const filteredMenuItems = menuItems?.filter(
    (menuItem) => regExp.test(menuItem.display) || regExp.test(menuItem.value),
  );

  const listItems = hasSearch ? filteredMenuItems : menuItems;

  const reset = () => {
    setSearchTerm('');
    setActiveMenuItemIndex(null);
  };

  const toggleDropdownMenu = () => {
    setIsMenuOpen((prevState) => !prevState);
    reset();
  };

  const handleClick = (menuItem) => {
    onMenuItemClick(menuItem);
    setIsMenuOpen(false);
    reset();
  };

  const handleChange = (event) => {
    const searchText = event.target.value;

    if (searchText.replace(/[\w -]/g, '')) {
      return;
    }

    setSearchTerm(searchText);
    setActiveMenuItemIndex(0);
  };

  const handleArrowDown = () => {
    if (isMenuOpen && activeMenuItemIndex === null) {
      setActiveMenuItemIndex(0);

      // in order to scroll up/down with keyboard arrows, we need to focus on the menu item first
      const firstElement = document.querySelector(
        `[data-qa="${dataQa}_menu_item_${snakeCase(listItems?.[0]?.display)}"]`,
      );
      if (firstElement) {
        (firstElement as HTMLElement).focus();
      }
    } else if (isMenuOpen && activeMenuItemIndex < listItems.length - 1) {
      setActiveMenuItemIndex((prevState) => prevState + 1);
    }
  };

  const handleArrowUp = () => {
    if (isMenuOpen && activeMenuItemIndex !== 0) {
      setActiveMenuItemIndex((prevState) => prevState - 1);
    }
  };

  const handleEnterKey = () => {
    if (isMenuOpen && listItems.length > 0 && activeMenuItemIndex !== null) {
      handleClick(listItems[activeMenuItemIndex]);
      reset();
    }
  };

  const handleEscapeKey = () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  useKeyDown('ArrowDown', handleArrowDown);
  useKeyDown('ArrowUp', handleArrowUp);
  useKeyDown('Enter', handleEnterKey);
  useKeyDown('Escape', handleEscapeKey);

  const hasValue = !!selection?.display;

  return (
    <CustomDropdownMenuSelector.Wrapper $hasError={hasFormikError} ref={dropdownMenuRef}>
      <CustomDropdownMenuSelector.Button
        disabled={disabled}
        onClick={toggleDropdownMenu}
        aria-haspopup="true"
        aria-expanded={isMenuOpen}
        data-qa={`${dataQa}_button`}
        $hasError={hasFormikError}
        $darkTheme={darkTheme}
        onBlur={onBlur}
        name={name}
        type="button"
      >
        <CustomDropdownMenuSelector.Label isFloating={hasValue}>
          {label}
        </CustomDropdownMenuSelector.Label>
        <CustomDropdownMenuSelector.Selection data-qa={`${dataQa}_selection`}>
          {displayValue || selection?.display}
        </CustomDropdownMenuSelector.Selection>
        <CustomDropdownMenuSelector.Caret
          direction={isMenuOpen ? 'up' : 'down'}
          $darkTheme={darkTheme}
        />
      </CustomDropdownMenuSelector.Button>
      {hasFormikError && (
        <CustomDropdownMenuSelector.Error
          name={name}
          component="span"
          data-qa={`${dataQa}_error`}
        />
      )}

      {isMenuOpen && (
        <CustomDropdownMenuSelector.Menu
          data-qa={`${dataQa}_menu`}
          hasSelection={!!selection?.value}
          $darkTheme={darkTheme}
        >
          {hasSearch && (
            <CustomDropdownMenuSelector.SearchBox $darkTheme={darkTheme}>
              <CustomDropdownMenuSelector.SearchInput
                autoFocus
                onChange={handleChange}
                placeholder="Search"
                value={searchTerm}
                $darkTheme={darkTheme}
              />
            </CustomDropdownMenuSelector.SearchBox>
          )}
          <CustomDropdownMenuSelector.MenuItemsWrapper data-qa={`${dataQa}_menu_wrapper`}>
            {isLoading && (
              <CustomDropdownMenuSelector.LoaderWrapper>
                <Loader style={{ width: '70%', margin: 'auto' }} />
              </CustomDropdownMenuSelector.LoaderWrapper>
            )}
            {listItems.map((menuItem, index) => {
              const isSelected = selection?.value === menuItem?.value;
              return (
                <CustomDropdownMenuSelector.MenuItem
                  aria-checked={isSelected}
                  aria-selected={isSelected}
                  key={menuItem.value}
                  onClick={() => handleClick(menuItem)}
                  isActive={index === activeMenuItemIndex}
                  data-qa={`${dataQa}_menu_item_${snakeCase(menuItem?.display)}`}
                  tabIndex={index}
                  $darkTheme={darkTheme}
                >
                  <span>{menuItem?.display}</span>
                  {isSelected && <CustomDropdownMenuSelector.Checkmark $darkTheme={darkTheme} />}
                </CustomDropdownMenuSelector.MenuItem>
              );
            })}
          </CustomDropdownMenuSelector.MenuItemsWrapper>
          {searchTerm && listItems.length === 0 && (
            <CustomDropdownMenuSelector.NoResults $darkTheme={darkTheme}>
              {notFoundMessage}
            </CustomDropdownMenuSelector.NoResults>
          )}
        </CustomDropdownMenuSelector.Menu>
      )}
    </CustomDropdownMenuSelector.Wrapper>
  );
};

CustomDropdownMenuSelector.Checkmark = styled(Checkmark)<{
  $darkTheme: ICustomDropdownMenuSelector['darkTheme'];
}>`
  line {
    stroke: ${({ theme, $darkTheme }) =>
      $darkTheme ? theme.colors.white : theme.navigation.textColor};
  }
`;

CustomDropdownMenuSelector.Wrapper = styled.div<{ $hasError: boolean }>`
  display: none;
  position: relative;

  @media (hover: hover) and (pointer: fine) {
    display: block;
  }
`;

CustomDropdownMenuSelector.Button = styled.button<{
  $hasError: boolean;
  $darkTheme: ICustomDropdownMenuSelector['darkTheme'];
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;
  padding: 4px 0 8px;
  border-bottom: ${({ theme, $darkTheme }) =>
    $darkTheme ? theme.borders.white : theme.navigation.border};
  margin-bottom: ${({ $hasError }) => ($hasError ? '4px' : '0')};

  :active {
    border-bottom: ${({ theme, $darkTheme }) =>
      $darkTheme ? theme.borders.white : theme.navigation.border};
  }
`;

CustomDropdownMenuSelector.Label = styled.label<{ isFloating: boolean }>`
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  cursor: pointer;
  transition: 0.2s ease all;

  ${({ isFloating }) =>
    isFloating &&
    css`
      position: absolute;
      transform: translateY(-16px);
      ${variableFontRegular({ size: '9.5px', height: '12px' })};
    `};
`;

CustomDropdownMenuSelector.Selection = styled.div`
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
`;

CustomDropdownMenuSelector.Error = styled(ErrorMessage)`
  color: ${({ theme }) => theme.colors.error};
  ${variableFontRegular({ size: '9.5px', height: '9px' })};
  margin-top: 8px;
`;

CustomDropdownMenuSelector.Menu = styled.div<{
  hasSelection: boolean;
  $darkTheme: ICustomDropdownMenuSelector['darkTheme'];
}>`
  position: absolute;
  top: 26px;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 340px;
  overflow: hidden;
  z-index: ${({ theme }) => theme.zIndex.dropdownSelectMenu};
  border: ${({ theme, $darkTheme }) =>
    $darkTheme ? theme.borders.white : theme.navigation.border};
  background: ${({ theme, $darkTheme }) =>
    $darkTheme ? theme.colors.black : theme.navigation.backgroundColor};
  -webkit-transform: translate3d(0, 0, 0); // fixes BUY3-1669
`;

CustomDropdownMenuSelector.SearchBox = styled.div<{
  $darkTheme: ICustomDropdownMenuSelector['darkTheme'];
}>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: ${({ theme, $darkTheme }) =>
    $darkTheme ? theme.borders.white : theme.navigation.border};
`;

CustomDropdownMenuSelector.SearchInput = styled.input<{
  $darkTheme: ICustomDropdownMenuSelector['darkTheme'];
}>`
  border: none;
  width: 100%;
  outline: none;
  padding: 0;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  ::placeholder {
    color: ${({ theme, $darkTheme }) =>
      $darkTheme ? theme.colors.white : theme.navigation.textColor};
  }
`;

CustomDropdownMenuSelector.MenuItemsWrapper = styled(UnorderedList)`
  overflow-y: scroll;
  flex: 1;
`;

CustomDropdownMenuSelector.MenuItem = styled.li<{
  isActive: boolean;
  $darkTheme: ICustomDropdownMenuSelector['darkTheme'];
}>`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 16px;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  color: ${({ theme, $darkTheme }) =>
    $darkTheme ? theme.colors.white : theme.navigation.textColor};
  text-decoration: ${({ isActive }) => isActive && 'underline'};
  text-underline-position: under;

  :first-child {
    padding-top: 12px;
  }

  :last-child {
    padding-bottom: 12px;
  }

  :hover {
    text-decoration: underline;
    text-underline-position: under;
  }

  :active {
    outline: none;
  }
`;

CustomDropdownMenuSelector.LoaderWrapper = styled.div`
  padding: 16px;
`;

CustomDropdownMenuSelector.NoResults = styled.div<{
  $darkTheme: ICustomDropdownMenuSelector['darkTheme'];
}>`
  color: ${({ theme, $darkTheme }) =>
    $darkTheme ? theme.colors.white : theme.navigation.textColor};
  padding: 12px 16px;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
`;

CustomDropdownMenuSelector.Caret = styled(Caret)<{
  $darkTheme: ICustomDropdownMenuSelector['darkTheme'];
}>`
  path {
    stroke: ${({ theme, $darkTheme }) =>
      $darkTheme ? theme.colors.white : theme.navigation.textColor};
  }
`;
